/**
 * Which state pill and which primary action the panel shows.
 *
 * IN A LIST the answer is about THIS list's row, never the person-level
 * `state.state`: 03 marks a person `added` when ANY list exported them, so a
 * person added to search A would read "Added" over a list linked to search B
 * (03's Blast radius). The row wins, in this order:
 *
 *   1. the contacts-table row itself, when it is on the current page — the
 *      table and the panel then read the SAME cache entry, so after an action
 *      they cannot disagree (PRD failure case "stale action state");
 *   2. 03's `current_list`, which applies the same `review_status`
 *      derivations server-side (for a `?person=` not on this page).
 *
 * OUTSIDE a list (the standalone page) there is no row to act on, so the
 * person-level state is shown, without an action.
 */
import type { ImportContactRow, PersonDetail, PersonListContext } from "@nu-assist/contracts";

export type PanelState =
  | "never_compared"
  | "new"
  | "conflict"
  | "in_thrive"
  | "added"
  | "left_out"
  | "failed";

export function rowState(row: ImportContactRow): PanelState {
  switch (row.review_status) {
    case "ignored":
      return "left_out";
    case "exported":
      return "added";
    case "to_review":
      return "conflict";
    case "failed":
      return "failed";
    case "already_in_thrive":
      return "in_thrive";
    case "not_in_thrive":
      return "new";
    default:
      return "never_compared";
  }
}

export function currentListState(ctx: PersonListContext): PanelState {
  if (ctx.add_to_search_state === "added") return "added";
  if (ctx.skipped) return "left_out";
  if (ctx.add_to_search_state === "needs_review") return "conflict";
  if (ctx.add_to_search_state === "failed") return "failed";
  if (ctx.thrive_id_state === "found") return "in_thrive";
  if (ctx.thrive_id_state === "not_found") return "new";
  return "never_compared";
}

export interface ResolvedState {
  state: PanelState;
  /** The person's row in the list, for the per-contact endpoints; null outside a list. */
  importContactId: string | null;
  thriveContactId: string | null;
}

export function resolveState(
  person: PersonDetail,
  row: ImportContactRow | undefined
): ResolvedState {
  if (row) {
    return {
      state: rowState(row),
      importContactId: row.import_contact_id,
      thriveContactId: row.contact_id_thrive ?? person.state.thrive_contact_id ?? null,
    };
  }
  if (person.current_list) {
    return {
      state: currentListState(person.current_list),
      importContactId: person.current_list.import_contact_id,
      thriveContactId: person.state.thrive_contact_id ?? null,
    };
  }
  return {
    state: person.state.state,
    importContactId: null,
    thriveContactId: person.state.thrive_contact_id ?? null,
  };
}

/** The pill's words. `thriveId` completes the in-Thrive and conflict pills. */
export function stateLabel(state: PanelState, thriveId: string | null): string {
  switch (state) {
    case "never_compared":
      return "Not compared to Thrive yet";
    case "new":
      return "New · not in Thrive";
    case "conflict":
      return thriveId ? `Conflict · Thrive #${thriveId}` : "Conflict · needs review";
    case "in_thrive":
      return thriveId ? `In Thrive · #${thriveId}` : "In Thrive";
    case "added":
      return "Added to search";
    case "left_out":
      return "Left out of this list";
    case "failed":
      return "Add to search failed";
  }
}

export const STATE_PILL_CLASS: Record<PanelState, string> = {
  never_compared: "bg-warning-muted text-warning-ink",
  new: "bg-info-muted text-info",
  conflict: "bg-conflict-muted text-conflict",
  in_thrive: "bg-success-muted text-success-ink",
  added: "bg-success-muted text-success-ink",
  left_out: "bg-surface-raised text-text-secondary",
  failed: "bg-error-muted text-error-ink",
};
